import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Button,
  Form,
  Row,
  Col,
  Container,
  Spinner,
} from 'react-bootstrap';
import { FaFileExcel, FaSearch, FaBoxOpen } from 'react-icons/fa';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import DataTable from 'react-data-table-component';
import { authAxios, formatDateTime12Hr } from '../utils/reportUtils';

const CustomersReport = () => {
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dateFrom, setDateFrom] = useState(new Date().toISOString().slice(0, 10));
  const [dateTo, setDateTo] = useState(new Date().toISOString().slice(0, 10));
  const abortControllerRef = useRef(null);

  const fetchReport = useCallback(async () => {
    if (abortControllerRef.current) abortControllerRef.current.abort();
    abortControllerRef.current = new AbortController();

    setLoading(true);
    setReportData([]);

    try {
      const dTo = new Date(dateTo);
      dTo.setHours(23, 59, 59, 999);
      const dFrom = new Date(dateFrom);
      dFrom.setHours(0, 0, 0, 0);

      let allCustomers = [];
      let page = 1;
      let lastPage = 1;

      // Fetch Page Loop
      while (page <= lastPage) {
        if (abortControllerRef.current?.signal.aborted) break;

        const res = await authAxios.get('/customers', {
          params: { page, per_page: 50 },
          signal: abortControllerRef.current.signal
        });

        const responseData = res.data?.data; // standard resource response
        const customersData = Array.isArray(responseData?.data)
          ? responseData.data
          : Array.isArray(responseData)
            ? responseData
            : [];

        const metaLastPage = responseData?.last_page || responseData?.meta?.last_page || res.data?.last_page || 1;
        lastPage = metaLastPage;

        let stopFetching = false;
        const filteredPage = [];

        for (const customer of customersData) {
          const cDate = new Date(customer.created_at);

          if (cDate > dTo) continue;
          // Note: Customers might not be sorted by created_at desc by default in the controller (standard paginate is usually id asc or created_at asc?)
          // The controller says: Customer::paginate(20). Default is usually ID asc.
          // If ID asc, then older customers come first.
          // If we want "Customers Created" within range, we check range. 
          // We can't optimize "stopFetching" easily unless we know sort order.
          // We will verify sort order. If ID asc (creation asc), then:
          // if cDate < dFrom: continue (too old, keep going for newer)
          // if cDate > dTo: break (too new? no, if ID asc, newer are at end. So if too new, we can maybe stop? no, we need to check all if it's mixed or standard default)
          // SAFEST: Fetch ALL pages and filter.

          if (cDate >= dFrom && cDate <= dTo) {
            filteredPage.push(customer);
          }
        }

        // Optimization: If we assume ID represents time (mostly true), 
        // If sorting is ASC (default):
        // If we find customers NEWER than dTo, we can theoretically stop? No, we are fetching page 1..N.
        // Page 1 has ID 1..20 (Oldest). 
        // If Page 1 dates are < dFrom, we continue.
        // If Page N dates are > dTo, we stop.
        // Let's implement full scan for safety, or check first/last item of page.

        allCustomers = [...allCustomers, ...filteredPage];

        if (customersData.length === 0) break;
        page++;
      }

      // Sort by latest created
      allCustomers.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

      setReportData(allCustomers);

    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Error fetching customers report:', error);
      }
    } finally {
      if (!abortControllerRef.current?.signal.aborted) {
        setLoading(false);
      }
    }
  }, [dateFrom, dateTo]);

  useEffect(() => {
    fetchReport();
    return () => abortControllerRef.current?.abort();
  }, [fetchReport]);

  const handleSearch = () => {
    fetchReport();
  };

  const handleExportToExcel = () => {
    if (!reportData.length) {
      alert('No data to export');
      return;
    }

    const exportData = reportData.map((item) => ({
      'ID': item.id,
      'Customer Name': item.name,
      'Phone': item.phone,
      'Email': item.email || 'N/A',
      'Address': item.address || 'N/A',
      'Created At': formatDateTime12Hr(item.created_at),
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Customers Report');
    saveAs(
      new Blob([XLSX.write(wb, { bookType: 'xlsx', type: 'array' })], {
        type: 'application/octet-stream',
      }),
      `customers_created_${dateFrom}_to_${dateTo}.xlsx`
    );
  };

  const columns = [
    {
      name: 'ID',
      selector: (row) => row.id,
      sortable: true,
      width: '80px',
    },
    {
      name: 'Customer Name',
      selector: (row) => row.name,
      sortable: true,
    },
    {
      name: 'Phone',
      selector: (row) => row.phone,
      sortable: true,
    },
    {
      name: 'Email',
      selector: (row) => row.email || 'N/A',
      sortable: true,
    },
    {
      name: 'Address',
      selector: (row) => row.address || 'N/A',
      wrap: true,
    },
    {
      name: 'Created At',
      selector: (row) => formatDateTime12Hr(row.created_at),
      sortable: true,
    },
  ];

  const CustomLoadingIndicator = (
    <div className='p-3 text-center'>
      <Spinner
        animation='border'
        role='status'
        className='me-2'
        variant='primary'
      />
      <span className='text-primary'>Loading customers...</span>
    </div>
  );

  const CustomNoDataComponent = (
    <div className='p-4 text-center text-muted'>
      <FaBoxOpen className='mb-2' size={32} />
      <h5 className='mt-2'>No Data Found</h5>
      <p className='mb-0'>
        No customers created in this date range.
      </p>
    </div>
  );

  return (
    <Container fluid>
      <h3 style={{ textAlign: 'center', marginBottom: '20px' }}>
        Customers Report
      </h3>

      {/* Filters */}
      <Row className='justify-content-between align-items-center mt-3 mb-3'>
        <Col md={8}>
          <div className='d-flex align-items-center gap-3'>
            <Form.Control
              type='date'
              value={dateFrom}
              onChange={(e) => {
                setLoading(true);
                setDateFrom(e.target.value);
              }}
              style={{ width: '160px' }}
              title="Start Date"
            />
            <span className="text-muted">to</span>
            <Form.Control
              type='date'
              value={dateTo}
              onChange={(e) => {
                setLoading(true);
                setDateTo(e.target.value);
              }}
              style={{ width: '160px' }}
              title="End Date"
            />
            <Button
              variant='primary'
              size='sm'
              onClick={handleSearch}
              disabled={loading}
            >
              <FaSearch className='me-1' />
              Refresh
            </Button>
          </div>
        </Col>

        <Col md={4} className='text-end'>
          <Button size='sm' variant='success' onClick={handleExportToExcel}>
            <FaFileExcel /> Export
          </Button>
        </Col>
      </Row>

      {/* Table */}
      <div style={{ maxHeight: '59vh', position: 'relative' }}>
        {reportData.length > 0 && (
          <div
            className='text-end p-2'
            style={{
              backgroundColor: '#f8f9fa',
              borderBottom: '1px solid #dee2e6',
            }}
          >
            <small className='text-muted'>
              Showing <strong>{1}</strong> to <strong>{reportData.length}</strong>{' '}
              out of <strong>{reportData.length}</strong> customers
            </small>
          </div>
        )}
        <DataTable
          columns={columns}
          data={reportData}
          pagination={false}
          highlightOnHover
          striped
          dense
          persistTableHead
          fixedHeader
          fixedHeaderScrollHeight='59vh'
          progressPending={loading}
          progressComponent={CustomLoadingIndicator}
          noDataComponent={CustomNoDataComponent}
          customStyles={{
            table: {
              style: {
                tableLayout: 'auto',
                width: '100%',
                maxWidth: '100%',
              },
            },
            tableWrapper: {
              style: {
                width: '100%',
                maxWidth: '100%',
                overflowX: 'auto',
              },
            },
            headRow: {
              style: {
                backgroundColor: '#f8f9fa',
                borderBottomStyle: 'solid',
                borderBottomWidth: '2px',
                borderBottomColor: '#dee2e6',
              },
            },
            headCells: {
              style: {
                fontSize: '14px',
                fontWeight: '600',
                paddingLeft: '8px',
                paddingRight: '8px',
              },
            },
            cells: {
              style: {
                wordBreak: 'break-word',
                whiteSpace: 'normal',
              },
            },
          }}
        />
        {!loading && reportData.length > 0 && (
          <div className='text-center p-3 text-muted'>
            <small>All customers loaded ({reportData.length} total)</small>
          </div>
        )}
      </div>
    </Container>
  );
};

export default CustomersReport;
